import { Radio } from 'antd'
import classNames from 'classnames'

import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import Loader from '@/components/Atoms/Loader/Loader'
import { OWNER_ROLE_OPTIONS } from '@/constants'

import SelectNotFound from './SelectNotFound'
import {
  ORG_SCOPE_OPTIONS,
  TARGET_TYPE_OPTIONS,
} from './SubscriptionCreationModal.config'
import cn from './SubscriptionCreationModal.module.scss'
import type { SubscriptionTargetState } from './useSubscriptionTarget'

type SubscriptionTargetSectionProps = {
  target: SubscriptionTargetState
  popupClassName: string
  isOrganisationSelected: boolean
}

function SubscriptionTargetSection({
  target,
  popupClassName,
  isOrganisationSelected,
}: SubscriptionTargetSectionProps) {
  const { users, orgSearch } = target

  return (
    <section className={cn.section}>
      <h3 className={cn.sectionTitle}>2. Кому отправлять</h3>
      <Radio.Group
        className={cn.radioGroup}
        value={target.targetType}
        options={TARGET_TYPE_OPTIONS}
        onChange={(e) => target.changeTargetType(e.target.value)}
      />

      {target.targetType === 'USER' && (
        <>
          <Radio.Group
            className={cn.scopeRadio}
            value={target.orgScope}
            options={ORG_SCOPE_OPTIONS}
            onChange={(e) => target.changeOrgScope(e.target.value)}
          />

          {target.orgScope === 'CLIENT' && (
            <HoverElement>
              <AtomSelect
                isOrganisationSelected={isOrganisationSelected}
                openByHover={false}
                outlined
                showSearch
                filterOption={false}
                placeholder="Поиск организации..."
                value={target.orgValue}
                searchValue={orgSearch.searchValue}
                onSearch={orgSearch.handleSearch}
                onOpenChange={orgSearch.handleOpenChange}
                onPopupScroll={orgSearch.handlePopupScroll}
                loading={orgSearch.loading}
                handleChange={(id) => {
                  if (typeof id === 'string') target.selectOrg(id)
                }}
                handleClear={target.clearOrg}
                options={orgSearch.options}
                notFoundContent={<SelectNotFound loading={orgSearch.loading} />}
                popupClassName={popupClassName}
              />
            </HoverElement>
          )}

          {users.loading ? (
            <div className={cn.miniLoader}>
              <Loader size={16} />
            </div>
          ) : users.users.length > 0 ? (
            <div className={cn.userListFrame}>
              <ul className={cn.userList} onScroll={target.handleUsersScroll}>
                {users.users.map((user) => (
                  <li
                    key={user.id}
                    className={classNames(
                      cn.userItem,
                      target.selectedUser?.id === user.id && cn.userItemSelected
                    )}
                    onClick={() => target.setSelectedUser(user)}
                  >
                    <span className={cn.userItemName}>{user.description}</span>
                    <span className={cn.userItemLogin}>{user.login}</span>
                    <span
                      className={classNames(
                        cn.userItemTgDot,
                        user.telegramActivated
                          ? cn.userItemTgDotActive
                          : cn.userItemTgDotInactive
                      )}
                      title={
                        user.telegramActivated
                          ? 'Telegram активирован'
                          : 'Telegram не активирован'
                      }
                    />
                  </li>
                ))}
                {users.loadingMore && (
                  <li className={cn.userListLoader}>
                    <Loader size={14} />
                  </li>
                )}
              </ul>
            </div>
          ) : target.orgScope === 'MY' || target.selectedOrg ? (
            <div className={cn.emptyHint}>Пользователи не найдены</div>
          ) : null}
        </>
      )}

      {target.targetType === 'ROLE_GROUP' && (
        <HoverElement>
          <AtomSelect
            isOrganisationSelected={isOrganisationSelected}
            openByHover={false}
            outlined
            placeholder="Выберите роль"
            value={target.selectedRole ?? undefined}
            options={OWNER_ROLE_OPTIONS}
            handleChange={(role) => {
              if (typeof role === 'string') target.setSelectedRole(role)
            }}
            popupClassName={popupClassName}
          />
        </HoverElement>
      )}

      {target.targetType === 'CHAT_ID' && (
        <AtomFormInput
          isOrganisationSelected={isOrganisationSelected}
          location="subscriptionCreation"
          name="chatId"
          placeholder="Например: -100123456789"
          value={target.chatId}
          onChange={(e) => target.setChatId(e.target.value)}
          fullWidth
        />
      )}
    </section>
  )
}

export default SubscriptionTargetSection
